import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
/**
 * RemoteAppWrapper provides CSS isolation for remote micro-frontend applications.
 *
 * This component prevents CSS conflicts between the host app (Material UI)
 * and remote apps (Tailwind, Bootstrap, etc.) by:
 *
 * 1. Creating a scoped container with CSS isolation
 * 2. Intercepting and scoping dynamically injected styles from remote apps
 * 3. Adding data attributes to track style ownership
 *
 * The remote app's styles are prefixed with a unique selector to prevent
 * them from affecting the host application's styles.
 */
const RemoteAppWrapper = ({ children, appName = 'remote-app', enableStyleScoping = true }) => {
    const containerRef = useRef(null);
    const scopingPrefix = `[data-remote-app="${appName}"]`;
    useEffect(() => {
        if (!enableStyleScoping)
            return;
        // Create a protective style sheet FIRST to prevent remote styles from affecting host
        const protectionStyle = document.createElement('style');
        protectionStyle.dataset.protection = appName;
        protectionStyle.textContent = `
			/* Protect host app from remote app's global resets */
			body:not([data-remote-app="${appName}"]),
			html:not([data-remote-app="${appName}"]) {
				/* Preserve Material-UI defaults */
				font-family: "Open Sans", "Helvetica", "Arial", sans-serif !important;
				line-height: 1.43 !important;
			}
		`;
        document.head.appendChild(protectionStyle);
        // Mark all existing styles as host before remote app loads
        for (const style of document.querySelectorAll('style')) {
            if (!style.dataset.app &&
                !style.dataset.remoteApp &&
                !style.dataset.protection) {
                style.dataset.app = 'host';
            }
        }
        // Track styles created by this remote app for cleanup
        const remoteAppStyles = new Set();
        remoteAppStyles.add(protectionStyle);
        // Track styles we've processed to avoid re-scoping
        const processedStyles = new WeakSet();
        /**
         * Determines if a style element belongs to the host app
         * Host styles are identified by:
         * 1. Having data-app="host" attribute
         * 2. Having data-mui or other Material-UI markers
         * 3. Being injected by webpack style-loader for host
         */
        const isHostStyle = (styleElement) => {
            // Check explicit host marker
            if (styleElement.dataset.app === 'host') {
                return true;
            }
            // Check for Material-UI injected styles
            if (styleElement.dataset.mui ||
                styleElement.dataset.emotion ||
                styleElement.dataset.styled ||
                styleElement.dataset.jss) {
                // Mark it as host for future checks
                styleElement.dataset.app = 'host';
                return true;
            }
            // Check if the style element's content suggests it's from Material-UI
            try {
                const content = styleElement.textContent ?? '';
                if (content.includes('.MuiBox-') ||
                    content.includes('.MuiStack-') ||
                    content.includes('.MuiTypography-') ||
                    content.includes('.MuiButton-') ||
                    content.includes('.MuiAppBar-') ||
                    content.includes('.MuiDrawer-') ||
                    content.includes('.MuiPaper-') ||
                    content.includes('.MuiMenu-') ||
                    content.includes('.MuiSkeleton-') ||
                    content.includes('.MuiDivider-') ||
                    content.includes('.MuiIconButton-') ||
                    content.includes('.Mui')) {
                    styleElement.dataset.app = 'host';
                }
            }
            catch {
                // If we can't read content, assume it's host to be safe
                return true;
            }
            return false;
        };
        /**
         * Processes a CSS style rule by scoping its selector
         */
        const processCSSStyleRule = (rule, scopingPrefix) => {
            const selector = rule.selectorText;
            // Block global resets
            const globalResets = [
                '*',
                '*, ::before, ::after',
                '*, *::before, *::after',
                'html',
                'body',
                ':root'
            ];
            if (globalResets.includes(selector)) {
                return ''; // Skip global resets
            }
            // Scope the selector
            const scopedSelector = selector
                .split(',')
                .map((sel) => `${scopingPrefix} ${sel.trim()}`)
                .join(', ');
            return `${scopedSelector} { ${rule.style.cssText} }`;
        };
        /**
         * Processes a CSS media rule by scoping its nested rules
         */
        const processCSSMediaRule = (rule, scopingPrefix) => {
            const mediaRules = [];
            for (const mediaRule of rule.cssRules) {
                if (mediaRule instanceof CSSStyleRule) {
                    const scopedSelector = mediaRule.selectorText
                        .split(',')
                        .map((selector) => `${scopingPrefix} ${selector.trim()}`)
                        .join(', ');
                    mediaRules.push(`${scopedSelector} { ${mediaRule.style.cssText} }`);
                }
            }
            return `@media ${rule.conditionText} { ${mediaRules.join(' ')} }`;
        };
        /**
         * Processes all CSS rules in a stylesheet
         */
        const processStyleRules = (sheet, scopingPrefix) => {
            const rules = [];
            for (const rule of sheet.cssRules) {
                if (rule instanceof CSSStyleRule) {
                    const processedRule = processCSSStyleRule(rule, scopingPrefix);
                    if (processedRule) {
                        rules.push(processedRule);
                    }
                }
                else if (rule instanceof CSSMediaRule) {
                    rules.push(processCSSMediaRule(rule, scopingPrefix));
                }
                else {
                    // Keep other rules as-is (keyframes, font-face, etc.)
                    rules.push(rule.cssText);
                }
            }
            return rules;
        };
        /**
         * Scopes CSS rules by prefixing selectors with the container's data attribute
         */
        const scopeStyleElement = (styleElement) => {
            // Skip if already processed
            if (processedStyles.has(styleElement))
                return;
            // Skip if it's a host app style
            if (isHostStyle(styleElement))
                return;
            // Skip if already scoped
            if (styleElement.dataset.scoped) {
                return;
            }
            try {
                const sheet = styleElement.sheet;
                if (!sheet) {
                    // Mark as processed even if we can't access the sheet
                    processedStyles.add(styleElement);
                    return;
                }
                // Use helper function to process all rules
                const rules = processStyleRules(sheet, scopingPrefix);
                // Replace the original styles with scoped ones
                if (rules.length > 0) {
                    styleElement.textContent = rules.join('\n');
                    styleElement.dataset.remoteApp = appName;
                    styleElement.dataset.scoped = 'true';
                    processedStyles.add(styleElement);
                    remoteAppStyles.add(styleElement);
                }
            }
            catch (error) {
                // CORS or other access errors - mark as processed to avoid retry
                console.warn(`Could not scope style element for ${appName}:`, error);
                processedStyles.add(styleElement);
            }
        };
        /**
         * Handle mutation observer callback for new style elements
         */
        const handleMutations = (mutations) => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeName === 'STYLE') {
                        const styleElement = node;
                        // Give the browser time to parse the stylesheet
                        setTimeout(() => scopeStyleElement(styleElement), 0);
                    }
                }
            }
        };
        /**
         * Watch for new style elements being added to the DOM
         */
        const observer = new MutationObserver(handleMutations);
        // Watch for new styles
        observer.observe(document.head, {
            childList: true,
            subtree: true
        });
        // Cleanup function: remove remote app styles when unmounting
        return () => {
            observer.disconnect();
            // Remove all styles created by this remote app
            for (const style of remoteAppStyles) {
                style.remove();
            }
            remoteAppStyles.clear();
        };
    }, [enableStyleScoping, appName, scopingPrefix]);
    return (_jsx(Box, { ref: containerRef, "data-remote-app": appName, sx: {
            // Establish a fresh styling context for the remote app
            // This resets all inherited styles
            position: 'relative',
            display: 'block',
            width: '100%',
            minHeight: '100vh',
            height: '100%',
            // CSS isolation to create a new stacking context
            isolation: 'isolate',
            // Reset box model
            boxSizing: 'border-box',
            margin: 0,
            padding: 0,
            // Remove contain property to allow proper overflow and positioning
            overflow: 'auto',
            // Reset some common properties that might conflict
            '& *': {
                boxSizing: 'border-box'
            },
            // Reset Typography that might be inherited from Material UI
            '& p, & h1, & h2, & h3, & h4, & h5, & h6': {
                margin: 0
            }
        }, children: children }));
};
export default RemoteAppWrapper;
